import React, { useEffect, useState } from "react";
import customLocalStorage from "./common/local-storage";
import TopMenu from "./navigation/top-menu";
import Login from "./user-management/login";
import CollectedWaste from "./waste-collection/collected-waste";
import Users from "./user-management/users";
import Partners from "./partner-management/partners";
import Settings from "./settings/index";
import BottomNavBar from "./navigation/footer";
import Alert from "@material-ui/lab/Alert";
import Snackbar from "@material-ui/core/Snackbar";
import Backdrop from "@material-ui/core/Backdrop";
import { CircularProgress } from "@material-ui/core";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Avatar from "@material-ui/core/Avatar";
import Typography from "@material-ui/core/Typography";
import RestoreFromTrashIcon from "@material-ui/icons/RestoreFromTrash";
import debounce from "lodash.debounce";
import moment from "moment";
import jwt from "jwt-decode";
import apiRequest from "./common/api-request";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    overflow: "hidden",
    padding: theme.spacing(0.5, 0.5),
  },
  paper: {
    margin: `${theme.spacing(1)}px auto`,
    padding: theme.spacing(1),
    cursor: "pointer",
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const Home = () => {
  const classes = useStyles();
  const initialState = {
    menu: customLocalStorage.decrypt("iwmLastMenu") ?? "Home",
    displaySnackBar: false,
    snackBarMessage: "",
    snackBarautoHideDuration: 2000, //milliseconds
    snackBarVerticalPosition: "top",
    snackBarHorizontalPosition: "right",
    snackBarSeverity: "info",
    loading: false,
    isSignedIn: false,
    autoLogOutTriggered: false,
  };
  const [state, setState] = useState(initialState);
  const {
    menu,
    displaySnackBar,
    snackBarMessage,
    snackBarautoHideDuration,
    snackBarVerticalPosition,
    snackBarHorizontalPosition,
    snackBarSeverity,
    loading,
    isSignedIn,
  } = state;

  const user = customLocalStorage.decrypt("iwmUser");

  //Record user activity
  if (user) {
    user.lastTimeActive = moment();
    customLocalStorage.encrypt("iwmUser", JSON.stringify(user));
  }

  const handleRefreshToken = (params) => {
    const { data, status, severity } = params;
    if (status === 401) handleLogOut({ msg: data, severity });
    if (!data) return;
    const { access } = data;
    if (!access) return;
    const { exp } = jwt(access);
    if (!exp) return;
    const user = customLocalStorage.decrypt("iwmUser");
    user.exp = exp;
    user.access = access;
    customLocalStorage.encrypt("iwmUser", JSON.stringify(user));
  };

  const handleGetUserDetails = (params) => {
    // console.log({ params });
    const { data, severity } = params;
    if (severity === `success`) {
      const user = customLocalStorage.decrypt("iwmUser");
      customLocalStorage.encrypt(
        "iwmUser",
        JSON.stringify({ ...user, ...data[0] })
      );
    }
  };

  const refreshToken = async () => {
    const user = customLocalStorage.decrypt("iwmUser");
    const { refresh } = user;
    const params = {
      url: `${process.env.REACT_APP_API_URL}/auth/token/refresh`,
      data: {
        refresh,
      },
      callBack: handleRefreshToken,
      method: `post`,
      handleLoader,
    };
    await apiRequest(params);
  };

  const checkTokenExpiry = async () => {
    const user = customLocalStorage.decrypt("iwmUser");
    const { exp } = user || {};
    // console.log({ exp });
    const currentTimestamp = moment().unix();
    // console.log({ currentTimestamp });
    if (currentTimestamp > exp) {
      await refreshToken();
    }
  };

  const getUserDetails = async (user_id) => {
    const params = {
      url: `${process.env.REACT_APP_API_URL}/users/${user_id}`,
      data: {},
      callBack: handleGetUserDetails,
      method: `get`,
      handleLoader: () => {
        // console.info(`getUserDetails`);
      },
    };
    await apiRequest(params);
  };

  const autoLogOut = debounce(async () => {
    const user = customLocalStorage.decrypt("iwmUser");
    const duration = moment
      .duration(moment().diff(user?.lastTimeActive))
      .asMinutes();
    const setTimeOut = process.env.REACT_APP_AUTOLOGOUT_DURATION;
    if (user?.lastTimeActive && duration > setTimeOut) {
      handleLogOut({ msg: `Auto Logout`, severity: `warning` });
    }
    await autoLogOut();
    await checkTokenExpiry();
    if (user?.user_id && !!user?.otpverified) {
      await getUserDetails(user?.user_id);
    }
  }, 80000);

  const showNotification = (msg, severity) => {
    if (severity.toLowerCase() === "error" && msg.search("401") !== -1) {
      handleLogOut({ msg: "Unauthorized", severity: "error" });
      return;
    }

    if (severity.toLowerCase() === "error" && msg.search("403") !== -1) {
      handleLogOut({ msg: "Token expired", severity: "error" });
      return;
    }

    setState((prevState) => ({
      ...prevState,
      displaySnackBar: true,
      snackBarMessage: msg,
      snackBarSeverity: severity,
    }));
  };

  const handleMenuChange = (params) => {
    const { menu } = params;
    setState((prevState) => ({
      ...prevState,
      menu,
    }));
    customLocalStorage.encrypt("iwmLastMenu", menu);
    if (menu === `Logout`) {
      handleLogOut();
    }
  };

  const handleLogOut = (params) => {
    let msg = params?.msg ? params?.msg : "Signed out";
    let severity = params?.severity ? params?.severity : "info";
    localStorage.clear();
    setState((prevState) => ({
      ...prevState,
      isSignedIn: false,
      menu: "Home",
      displaySnackBar: true,
      snackBarMessage: msg,
      snackBarSeverity: severity,
    }));
  };

  const handleSignIn = async (params) => {
    const { data, status, severity } = params;
    // console.log({ data });
    let snackBarMessage = "Signed in";
    let isSignedIn = false;
    if (severity === "error") {
      snackBarMessage = data;
    } else {
      let user = customLocalStorage.decrypt("iwmUser");
      if (
        data?.otpverified === "True" &&
        status === 200 &&
        user?.user_id == jwt(data?.access || "")?.user_id
      ) {
        isSignedIn = true;
        user = { ...user, ...data, otpverified: true };
        customLocalStorage.encrypt("iwmUser", JSON.stringify(user));
        await getUserDetails(user?.user_id);
      } else {
        user = { ...data, ...jwt(data?.access || "") };
        data.lastTimeActive = moment();
        customLocalStorage.encrypt("iwmUser", JSON.stringify(user));
        isSignedIn = JSON.parse(process.env.REACT_APP_IGNORE_OTP_CHECK);
      }
      handleMenuChange({ menu: "WasteCollection" });
    }
    setState((prevState) => ({
      ...prevState,
      isSignedIn,
    }));
    showNotification(snackBarMessage, severity);
  };

  const handleCloseSnackBar = () => {
    setState((prevState) => ({
      ...prevState,
      displaySnackBar: false,
      isLoading: false,
    }));
  };

  const handleLoader = (action) => {
    setState((prevState) => ({
      ...prevState,
      loading: action,
    }));
  };

  useEffect(() => {
    autoLogOut();
    if (user?.user_id) {
      setState((prevState) => ({
        ...prevState,
        isSignedIn: user?.otpverified,
      }));
    } else {
      localStorage.clear();
    }
  }, []);

  return (
    <>
      <TopMenu
        menu={menu}
        handleMenuChange={handleMenuChange}
        isSignedIn={isSignedIn}
      />
      <div className={classes.root}>
        <Backdrop className={classes.backdrop} open={loading}>
          <CircularProgress />
        </Backdrop>
        <Snackbar
          anchorOrigin={{
            vertical: snackBarVerticalPosition,
            horizontal: snackBarHorizontalPosition,
          }}
          open={displaySnackBar}
          onClose={handleCloseSnackBar}
          autoHideDuration={snackBarautoHideDuration}
          // message={snackBarMessage}
        >
          <Alert onClose={handleCloseSnackBar} severity={snackBarSeverity}>
            {snackBarMessage}
          </Alert>
        </Snackbar>

        {!isSignedIn && (
          <Login handleLoader={handleLoader} handleSignIn={handleSignIn} />
        )}

        {isSignedIn && menu === `WasteCollection` && (
          <CollectedWaste
            handleLoader={handleLoader}
            showNotification={showNotification}
            handleLogOut={handleLogOut}
          />
        )}

        {isSignedIn && user?.is_admin && menu === `UserManagement` && (
          <Users
            handleLoader={handleLoader}
            showNotification={showNotification}
            handleLogOut={handleLogOut}
          />
        )}

        {isSignedIn && user?.is_admin && menu === `PartnerManagement` && (
          <Partners
            handleLoader={handleLoader}
            showNotification={showNotification}
            handleLogOut={handleLogOut}
          />
        )}

        {isSignedIn && user?.is_admin && menu === `SettingsManagement` && (
          <Settings
            handleLoader={handleLoader}
            showNotification={showNotification}
            handleLogOut={handleLogOut}
          />
        )}

        {isSignedIn && menu === `Home` && (
          <div className={classes.root}>
            <Paper className={classes.paper}>
              <Grid container spacing={2}>
                <Grid item>
                  <Avatar>
                    <NotificationsActiveIcon />
                  </Avatar>
                </Grid>
                <Grid item xs>
                  <Typography noWrap>
                    {process.env.REACT_APP_WELCOME}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
            <Paper
              className={classes.paper}
              onClick={() => {
                handleMenuChange({ menu: "WasteCollection" });
              }}
            >
              <Grid container spacing={2}>
                <Grid item>
                  <Avatar>
                    <RestoreFromTrashIcon />
                  </Avatar>
                </Grid>
                <Grid item xs>
                  <Typography noWrap>Waste Collection</Typography>
                </Grid>
              </Grid>
            </Paper>
          </div>
        )}
      </div>
      <BottomNavBar />
    </>
  );
};
export default Home;
